import { CAP_MARKET_CANISTER, ENTREPOT_PROXY, ICP_CANISTER_RE } from "./constants.js";
import { fetchDgdgText } from "./dgdgFetch.js";
import { createIcpAgent, normalizeIcpCanisterId } from "./icAgent.js";
import { principalToAccountId } from "./icpAccountId.js";
import { resolveCollectionDisplayName } from "./collectionNames.js";
const CANISTER_FROM_TEXT = /([a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{5}-cai)/gi;
let catalogCache = null;
const CATALOG_TTL_MS = 6 * 60 * 60 * 1000;
function extractCanisterIds(text) {
    const set = new Set();
    for (const m of text.matchAll(CANISTER_FROM_TEXT)) {
        const id = m[1]?.toLowerCase();
        if (id && id !== ENTREPOT_PROXY && ICP_CANISTER_RE.test(id))
            set.add(id);
    }
    return [...set];
}
/** Entrepot-listed collection canisters from the DGDG collections catalog. */
export async function fetchEntrepotCapCollectionCanisters() {
    const now = Date.now();
    if (catalogCache && now - catalogCache.at < CATALOG_TTL_MS) {
        return catalogCache.ids;
    }
    try {
        const text = await fetchDgdgText("/nfts/collections/__data.json?x-sveltekit-invalidated=01");
        const ids = extractCanisterIds(text);
        catalogCache = { at: now, ids };
        return ids;
    }
    catch {
        return catalogCache?.ids ?? [];
    }
}
function rowToNft(row, ownerPrincipal) {
    const canister = normalizeIcpCanisterId(String(row.canister ?? row.collection ?? ""));
    if (!canister)
        return null;
    const idxRaw = row.index ?? row.tokenIndex ?? row.tokenId;
    let tokenId = null;
    if (typeof idxRaw === "bigint")
        tokenId = idxRaw.toString();
    else if (typeof idxRaw === "number")
        tokenId = String(idxRaw);
    else if (typeof idxRaw === "string" && idxRaw.trim())
        tokenId = idxRaw.trim();
    if (!tokenId)
        return null;
    const numericIdx = /^\d+$/.test(tokenId) ? Number(tokenId) : null;
    const imageUrl = row.thumbnail ??
        (numericIdx != null
            ? `https://${canister}.raw.icp0.io/?index=${numericIdx}&type=thumbnail`
            : null);
    const collectionName = resolveCollectionDisplayName(canister);
    const tokenLabel = row.name ?? (numericIdx != null ? `#${numericIdx + 1}` : tokenId);
    return {
        chain: "icp",
        contractId: canister,
        tokenId,
        name: row.name && !row.name.startsWith(collectionName)
            ? `${collectionName} ${tokenLabel}`
            : row.name ??
                (numericIdx != null
                    ? `${collectionName} #${numericIdx + 1}`
                    : `${collectionName} ${tokenId}`),
        imageUrl,
        walletAddress: ownerPrincipal,
        collectionName,
    };
}
function parseCapWalletResponse(raw, ownerPrincipal) {
    const out = [];
    const seen = new Set();
    const visit = (value) => {
        if (!value)
            return;
        if (Array.isArray(value)) {
            for (const item of value)
                visit(item);
            return;
        }
        if (typeof value !== "object")
            return;
        const obj = value;
        const maybeNft = rowToNft(obj, ownerPrincipal);
        if (maybeNft) {
            const key = `${maybeNft.contractId}:${maybeNft.tokenId}`;
            if (!seen.has(key)) {
                seen.add(key);
                out.push(maybeNft);
            }
        }
        for (const v of Object.values(obj)) {
            if (v && typeof v === "object")
                visit(v);
        }
    };
    visit(raw);
    return out;
}
/** CAP market wallet index — supplements DGDG portfolio for Entrepot ecosystem NFTs. */
export async function fetchEntrepotCapPortfolioByPrincipal(ownerPrincipal) {
    if (!ownerPrincipal)
        return [];
    const accountId = await principalToAccountId(ownerPrincipal);
    const { Actor } = await import("@dfinity/agent");
    const { Principal } = await import("@dfinity/principal");
    const { IDL } = await import("@dfinity/candid");
    const agent = await createIcpAgent();
    const owner = Principal.fromText(ownerPrincipal);
    const TokenRow = IDL.Record({
        canister: IDL.Text,
        index: IDL.Nat32,
        thumbnail: IDL.Opt(IDL.Text),
        name: IDL.Opt(IDL.Text),
    });
    const idlFactory = () => IDL.Service({
        get_account_nfts: IDL.Func([IDL.Text], [IDL.Vec(TokenRow)], ["query"]),
        account_nfts: IDL.Func([IDL.Text], [IDL.Vec(TokenRow)], ["query"]),
        wallet_nfts: IDL.Func([IDL.Text], [IDL.Vec(TokenRow)], ["query"]),
        get_account_tokens: IDL.Func([IDL.Text], [IDL.Vec(TokenRow)], ["query"]),
        getAccountNfts: IDL.Func([IDL.Text], [IDL.Vec(TokenRow)], ["query"]),
        get_user_nfts: IDL.Func([IDL.Principal], [IDL.Vec(TokenRow)], ["query"]),
        getUserNfts: IDL.Func([IDL.Principal], [IDL.Vec(TokenRow)], ["query"]),
    });
    const actor = Actor.createActor(idlFactory, {
        agent,
        canisterId: CAP_MARKET_CANISTER,
    });
    const attempts = [
        () => actor.get_account_nfts?.(accountId),
        () => actor.account_nfts?.(accountId),
        () => actor.wallet_nfts?.(accountId),
        () => actor.get_account_tokens?.(accountId),
        () => actor.getAccountNfts?.(accountId),
        () => actor.get_user_nfts?.(owner),
        () => actor.getUserNfts?.(owner),
    ].filter(Boolean);
    for (const attempt of attempts) {
        try {
            const raw = await attempt();
            const nfts = parseCapWalletResponse(raw, ownerPrincipal);
            if (nfts.length > 0)
                return nfts;
            if (Array.isArray(raw) && raw.length > 0) {
                const mapped = [];
                for (const row of raw) {
                    const canister = normalizeIcpCanisterId(row.canister);
                    if (!canister)
                        continue;
                    const idx = Number(row.index);
                    const collectionName = resolveCollectionDisplayName(canister);
                    mapped.push({
                        chain: "icp",
                        contractId: canister,
                        tokenId: String(idx),
                        name: row.name?.[0] ?? `${collectionName} #${idx + 1}`,
                        imageUrl: row.thumbnail?.[0] ??
                            `https://${canister}.raw.icp0.io/?index=${idx}&type=thumbnail`,
                        walletAddress: ownerPrincipal,
                        collectionName,
                    });
                }
                if (mapped.length > 0)
                    return mapped;
            }
        }
        catch {
            /* try next method */
        }
    }
    return [];
}
